/**
 * MilestoneChip — the compact "where is this lead now" sub-line shown under the
 * lifecycle badge in the lead inbox, conversations, and board lists. Renders the
 * single server-derived milestone (see Milestone in types) as a small
 * icon + colored label. Returns null when there's no milestone, so callers can
 * drop it in unconditionally.
 */
import {
  CalendarCheck,
  CalendarClock,
  Clock,
  FileText,
  Hourglass,
  PhoneCall,
  PhoneOutgoing,
  Reply,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Milestone, MilestoneKey } from '../types';
import { MILESTONE_LABELS, relTime } from '../lib';

type Tone = 'urgent' | 'scheduled' | 'info' | 'muted';

/** Text-only tones (the chip sits under the badge, so it stays lightweight).
 *  Shades match the CRM's inline status-pill family (700 light / 300 dark). */
const TONE_CLS: Record<Tone, string> = {
  urgent: 'text-rose-700 dark:text-rose-300',
  scheduled: 'text-amber-700 dark:text-amber-300',
  info: 'text-blue-700 dark:text-blue-300',
  muted: 'text-gray-500 dark:text-gray-400',
};

/** Icon + tone per key; the label text lives in MILESTONE_LABELS (shared with
 *  the filter dropdown so the chip and the filter never drift). */
const META: Record<MilestoneKey, { tone: Tone; Icon: LucideIcon }> = {
  needs_reply: { tone: 'urgent', Icon: Reply },
  call_scheduled: { tone: 'scheduled', Icon: PhoneCall },
  call_overdue: { tone: 'urgent', Icon: PhoneCall },
  appt_scheduled: { tone: 'scheduled', Icon: CalendarCheck },
  needs_first_contact: { tone: 'info', Icon: PhoneOutgoing },
  follow_up_due: { tone: 'info', Icon: Clock },
  awaiting_records: { tone: 'info', Icon: FileText },
  ready_to_schedule: { tone: 'info', Icon: CalendarClock },
  going_stale: { tone: 'muted', Icon: Hourglass },
};

export default function MilestoneChip({
  milestone,
  className = '',
  hideKeys,
}: {
  milestone: Milestone | null | undefined;
  className?: string;
  /** Keys to render as nothing (e.g. the board hides the milestone that just
   *  restates the column the card already sits in). */
  hideKeys?: MilestoneKey[];
}) {
  if (!milestone) return null;
  if (hideKeys && hideKeys.includes(milestone.key)) return null;
  const meta = META[milestone.key];
  if (!meta) return null; // tolerate an unknown key from a newer server

  const { Icon } = meta;
  // The callback milestones fold in the live relative time ("Call in 2h",
  // "Call overdue · 3h"); everything else is a static label.
  let label = MILESTONE_LABELS[milestone.key];
  if (milestone.at && milestone.key === 'call_scheduled') {
    label = `Call ${relTime(milestone.at)}`;
  } else if (milestone.at && milestone.key === 'call_overdue') {
    label = `Call overdue · ${relTime(milestone.at)}`;
  }

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 text-xs ${TONE_CLS[meta.tone]} ${className}`}
      title={label}
    >
      <Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}
